import type {
  EmergencyContactRecord,
  FamilyPlanRecord,
  MeetingPointRecord,
  PreparednessChecklistRecord,
  ProtectedVaultRecord,
} from "@safesphere/contracts";

import { getOfflineDatabase } from "../offline/database.js";
import type { EncryptedVaultRecord, VaultMetadata } from "./storageTypes.js";
import {
  createVaultMaterial,
  decryptVaultRecord,
  encryptVaultRecord,
  unlockVaultMaterial,
} from "./vaultCrypto.js";

const sessionKeys = new Map<string, CryptoKey>();

export interface QuickPlanData {
  readonly plan: FamilyPlanRecord;
  readonly contacts: readonly EmergencyContactRecord[];
  readonly meetingPoints: readonly MeetingPointRecord[];
  readonly checklists: readonly PreparednessChecklistRecord[];
}

export async function saveQuickPlan(data: QuickPlanData): Promise<void> {
  const database = await getOfflineDatabase();
  const transaction = database.transaction(
    ["familyPlans", "familyContacts", "familyMeetingPoints", "familyChecklists"],
    "readwrite",
  );
  await transaction.objectStore("familyPlans").put(data.plan, data.plan.id);
  for (const record of data.contacts)
    await transaction.objectStore("familyContacts").put(record, record.id);
  for (const record of data.meetingPoints)
    await transaction.objectStore("familyMeetingPoints").put(record, record.id);
  for (const record of data.checklists)
    await transaction.objectStore("familyChecklists").put(record, record.id);
  await transaction.done;
}

export async function loadQuickPlan(planId: string): Promise<QuickPlanData | undefined> {
  const database = await getOfflineDatabase();
  const plan = await database.get("familyPlans", planId);
  if (plan === undefined) return undefined;
  const [contacts, meetingPoints, checklists] = await Promise.all([
    database.getAll("familyContacts"),
    database.getAll("familyMeetingPoints"),
    database.getAll("familyChecklists"),
  ]);
  return {
    plan,
    contacts: contacts.filter((record) => record.planId === planId),
    meetingPoints: meetingPoints.filter((record) => record.planId === planId),
    checklists: checklists.filter((record) => record.planId === planId),
  };
}

export async function initializeVault(planId: string, passphrase: string): Promise<void> {
  const material = await createVaultMaterial(planId, passphrase);
  await (await getOfflineDatabase()).put("vaultMetadata", material.metadata, planId);
  sessionKeys.set(planId, material.key);
}

export async function unlockVault(planId: string, passphrase: string) {
  const metadata = await (await getOfflineDatabase()).get("vaultMetadata", planId);
  if (metadata === undefined)
    return { ok: false as const, reason: "VAULT_NOT_INITIALIZED" as const };
  const result = await unlockVaultMaterial(metadata, passphrase);
  if (result.ok) sessionKeys.set(planId, result.key);
  return result;
}

export function lockVault(planId: string): void {
  sessionKeys.delete(planId);
}

export function lockAllVaultsForTests(): void {
  sessionKeys.clear();
}

export function isVaultUnlocked(planId: string): boolean {
  return sessionKeys.has(planId);
}

export async function hasVault(planId: string): Promise<boolean> {
  return (await (await getOfflineDatabase()).get("vaultMetadata", planId)) !== undefined;
}

export async function saveProtectedRecord(record: ProtectedVaultRecord): Promise<void> {
  const key = sessionKeys.get(record.planId);
  if (key === undefined) throw new Error("VAULT_LOCKED");
  const encrypted = await encryptVaultRecord(record, key);
  await (await getOfflineDatabase()).put("vaultRecords", encrypted, encrypted.id);
}

export async function loadProtectedRecords(
  planId: string,
): Promise<readonly ProtectedVaultRecord[]> {
  const key = sessionKeys.get(planId);
  if (key === undefined) throw new Error("VAULT_LOCKED");
  const encrypted = (await (await getOfflineDatabase()).getAll("vaultRecords")).filter(
    (record) => record.planId === planId,
  );
  return Promise.all(encrypted.map((record) => decryptVaultRecord(record, key)));
}

export async function readEncryptedVaultRecords(
  planId: string,
): Promise<readonly EncryptedVaultRecord[]> {
  return (await (await getOfflineDatabase()).getAll("vaultRecords")).filter(
    (record) => record.planId === planId,
  );
}

export async function readVaultMetadata(planId: string): Promise<VaultMetadata | undefined> {
  return (await getOfflineDatabase()).get("vaultMetadata", planId);
}

export async function replacePlanData(
  quick: QuickPlanData,
  metadata: VaultMetadata,
  encryptedRecords: readonly EncryptedVaultRecord[],
  sessionKey: CryptoKey,
): Promise<void> {
  const database = await getOfflineDatabase();
  const transaction = database.transaction(
    [
      "familyPlans",
      "familyContacts",
      "familyMeetingPoints",
      "familyChecklists",
      "vaultMetadata",
      "vaultRecords",
    ],
    "readwrite",
  );
  for (const [storeName, records] of [
    ["familyContacts", await transaction.objectStore("familyContacts").getAll()],
    ["familyMeetingPoints", await transaction.objectStore("familyMeetingPoints").getAll()],
    ["familyChecklists", await transaction.objectStore("familyChecklists").getAll()],
    ["vaultRecords", await transaction.objectStore("vaultRecords").getAll()],
  ] as const) {
    for (const record of records) {
      if (record.planId === quick.plan.id)
        await transaction.objectStore(storeName).delete(record.id);
    }
  }
  await transaction.objectStore("familyPlans").put(quick.plan, quick.plan.id);
  for (const record of quick.contacts)
    await transaction.objectStore("familyContacts").put(record, record.id);
  for (const record of quick.meetingPoints)
    await transaction.objectStore("familyMeetingPoints").put(record, record.id);
  for (const record of quick.checklists)
    await transaction.objectStore("familyChecklists").put(record, record.id);
  await transaction.objectStore("vaultMetadata").put(metadata, quick.plan.id);
  for (const record of encryptedRecords)
    await transaction.objectStore("vaultRecords").put(record, record.id);
  await transaction.done;
  sessionKeys.set(quick.plan.id, sessionKey);
}
